// useLLMAdapters.js — Fetch + cache + CRUD against Worker /api/adapters.
//
// Returns `{ adapters, loading, error, refresh, create, update, remove, test }`.
// Auto-refreshes after successful mutations.
//
// Sprint unified-models: cada componente tem instância PRÓPRIA deste hook
// (Chat, Code, Council, AdapterSheet). Pra sincronizar todas quando UMA
// salva um adapter (ex: configura API key), disparamos evento global
// `apolo:adaptersChanged` após mutação — todas as instâncias re-fetcham.
// Resultado: configurar 1 API → bolinha verde aparece em TODOS os dropdowns
// de TODAS as abas instantaneamente, sem reload.

import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api.js';
import { classifyProbeResult, writeProbeCache, providerOfAdapter, CONN_LABEL } from '../lib/models/connectedState.js';

const ADAPTERS_CHANGED_EVENT = 'apolo:adaptersChanged';

export function useLLMAdapters({ kind = null } = {}) {
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const path = kind ? `/api/adapters?kind=${encodeURIComponent(kind)}` : '/api/adapters';
    const res = await apiFetch(path);
    if (res.ok) {
      setAdapters(res.data?.adapters || []);
      setError(null);
    } else {
      setError(res.error || 'Failed to load adapters');
    }
    setLoading(false);
  }, [kind]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Sincronização cross-instância: re-fetch quando QUALQUER outra instância
  // do hook muta um adapter. Dispara o refresh local sem re-emitir o evento
  // (refresh não emite), evitando loop.
  useEffect(() => {
    function onChanged() { refresh(); }
    window.addEventListener(ADAPTERS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(ADAPTERS_CHANGED_EVENT, onChanged);
  }, [refresh]);

  // Notifica todas as instâncias após mutação bem-sucedida.
  const broadcastChange = useCallback(() => {
    try { window.dispatchEvent(new CustomEvent(ADAPTERS_CHANGED_EVENT)); } catch { /* noop */ }
  }, []);

  const create = useCallback(
    async (input) => {
      const res = await apiFetch('/api/adapters', { method: 'POST', body: input });
      if (res.ok) { await refresh(); broadcastChange(); }
      return res;
    },
    [refresh, broadcastChange],
  );

  const update = useCallback(
    async (id, patch) => {
      const res = await apiFetch(`/api/adapters/${id}`, { method: 'PATCH', body: patch });
      if (res.ok) { await refresh(); broadcastChange(); }
      return res;
    },
    [refresh, broadcastChange],
  );

  const remove = useCallback(
    async (id) => {
      const res = await apiFetch(`/api/adapters/${id}`, { method: 'DELETE' });
      if (res.ok) { await refresh(); broadcastChange(); }
      return res;
    },
    [refresh, broadcastChange],
  );

  const test = useCallback(async (id) => {
    const res = await apiFetch(`/api/adapters/${id}/test`, { method: 'POST' });
    // P4 — classifica o probe num estado RICO (no_access 403 / bad_key 401 / …) e
    // cacheia por provider. Mata o falso-verde do #645: chave presente mas a CONTA
    // sem acesso ao modelo (gpt-5.6-sol) mostrava "conectado". `res.data` = corpo do
    // testAdapter ({ok} | {ok:false, error:"HTTP 403 · …"}); apiFetch envelopa em res.
    const connState = classifyProbeResult(res?.data ?? res);
    const provider = providerOfAdapter(adapters.find((a) => a.id === id));
    if (provider) writeProbeCache(provider, connState, res?.data?.error || null);
    return { ...res, connState, connLabel: CONN_LABEL[connState] };
  }, [adapters]);

  // Modelos VIVOS do provider (pro "Sugerir modelos") → { ok, models } | { ok:false, error }.
  const listModels = useCallback(async (id) => {
    return apiFetch(`/api/adapters/${id}/models`, { method: 'POST' });
  }, []);

  return { adapters, loading, error, refresh, create, update, remove, test, listModels };
}
